import _ from 'lodash';
import PropTypes from 'prop-types';
import {
  CURRENCIES,
  DEFAULT_CURRENCY,
  DEFAULT_RATE_PERIOD,
  DEFAULT_ALLOCATION_PERIOD,
  REMOTE_WORK,
  B2B_LEVEL,
} from 'shared/constants';
import { buildInitialIntlAttribute } from 'shared/lib/intl';
import { normalizedDefaultProps, getNormalizedPropTypes } from './common';
import { propTypes as employmentPropTypes } from './Employment';
import { propTypes as fieldPropTypes } from './Field';
import { propTypes as rolePropTypes } from './Role';
import { propTypes as skillPropTypes } from './Skill';
import { propTypes as languagePropTypes } from './Language';
import { propTypes as locationPropTypes } from './Location';
import { propTypes as benefitPropTypes, propTypesIntl as benefitPropTypesIntl } from './Benefit';
import { propTypes as companyPropTypes } from './Company';

export const defaultProps = {
  id: '',
  companyId: '',
  slug: '',
  title: '',
  description: '',
  employmentTypes: [],
  partTimeMinimum: null,
  rate: null,
  rateUpTo: null,
  ratePeriod: DEFAULT_RATE_PERIOD,
  allocationDuration: null,
  allocationPeriod: DEFAULT_ALLOCATION_PERIOD,
  allocationExtendable: null,
  currency: DEFAULT_CURRENCY,
  homeoffice: 0,
  remoteWork: REMOTE_WORK.NO,
  maxLeadCostsCurrency: DEFAULT_CURRENCY,
  active: false,
  publishedAt: null,
  deactivatedAt: null,
  createdAt: '2017-01-01',
  updatedAt: null,
  roles: [],
  skills: [],
  educations: [],
  experience: [],
  languages: [],
  locations: [],
  benefits: [],
  suitableFor: [],
  btobLevel: B2B_LEVEL.ENABLED,
  score: null,
  leads: {},
  autoTranslate: true,
};

export const defaultPropsNormalized = {
  ...defaultProps,
  employmentTypes: normalizedDefaultProps,
  locations: normalizedDefaultProps,
  roles: normalizedDefaultProps,
  educations: normalizedDefaultProps,
  experience: normalizedDefaultProps,
  skills: normalizedDefaultProps,
  languages: normalizedDefaultProps,
  benefits: normalizedDefaultProps
};

export const defaultPropsIntl = {
  ...defaultProps,
  title: buildInitialIntlAttribute(''),
  description: buildInitialIntlAttribute(''),
};

export const defaultPropsNormalizedIntl = {
  ...defaultPropsNormalized,
  title: buildInitialIntlAttribute(''),
  description: buildInitialIntlAttribute(''),
};

export const propTypes = {
  id: PropTypes.string,
  companyId: PropTypes.string,
  slug: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  partTimeMinimum: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  rate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  rateUpTo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ratePeriod: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  monthlyRate: PropTypes.shape({
    [PropTypes.oneOf(CURRENCIES)]: PropTypes.number
  }),
  monthlyRateUpTo: PropTypes.shape({
    [PropTypes.oneOf(CURRENCIES)]: PropTypes.number
  }),
  allocationDuration: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  allocationPeriod: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  allocationExtendable: PropTypes.bool,
  currency: PropTypes.string,
  homeoffice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  remoteWork: PropTypes.oneOf(_.values(REMOTE_WORK)),
  active: PropTypes.bool,
  publishedAt: PropTypes.string,
  deactivatedAt: PropTypes.string,
  createdAt: PropTypes.string,
  updatedAt: PropTypes.string,
  roles: PropTypes.arrayOf(PropTypes.shape({
    ...rolePropTypes,
    field: PropTypes.shape(fieldPropTypes)
  })),
  employmentTypes: PropTypes.arrayOf(PropTypes.shape(employmentPropTypes)),
  skills: PropTypes.arrayOf(PropTypes.shape(skillPropTypes)),
  educations: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    level: PropTypes.number,
    required: PropTypes.bool
  })),
  experience: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    fieldId: PropTypes.number,
    roleId: PropTypes.number,
    months: PropTypes.number,
    required: PropTypes.bool
  })),
  languages: PropTypes.arrayOf(PropTypes.shape(languagePropTypes)),
  locations: PropTypes.arrayOf(PropTypes.shape(locationPropTypes)),
  benefits: PropTypes.arrayOf(PropTypes.shape(benefitPropTypes)),
  suitableFor: PropTypes.arrayOf(PropTypes.string),
  btobLevel: PropTypes.number,
  score: PropTypes.number,
  autoTranslate: PropTypes.bool,
};

export const propTypesIntl = {
  ...propTypes,
  title: PropTypes.shape(buildInitialIntlAttribute(PropTypes.string)),
  description: PropTypes.shape(buildInitialIntlAttribute(PropTypes.string)),
  benefits: PropTypes.arrayOf(PropTypes.shape(benefitPropTypesIntl)),
};

export const propTypesNormalized = {
  ...propTypes,
  locations: getNormalizedPropTypes(locationPropTypes),
  roles: getNormalizedPropTypes({
    ...rolePropTypes,
    field: PropTypes.shape(fieldPropTypes)
  }),
  employmentTypes: getNormalizedPropTypes(employmentPropTypes),
  skills: getNormalizedPropTypes(skillPropTypes),
  languages: getNormalizedPropTypes(languagePropTypes),
  benefits: getNormalizedPropTypes(benefitPropTypes),
  educations: getNormalizedPropTypes({
    id: PropTypes.string,
    level: PropTypes.number,
    required: PropTypes.bool,
  }),
  experience: getNormalizedPropTypes({
    id: PropTypes.string,
    fieldId: PropTypes.number,
    roleId: PropTypes.number,
    months: PropTypes.number,
    required: PropTypes.bool,
  })
};

export const propTypesNormalizedIntl = {
  ...propTypesNormalized,
  title: PropTypes.shape(buildInitialIntlAttribute(PropTypes.string)),
  description: PropTypes.shape(buildInitialIntlAttribute(PropTypes.string)),
  benefits: getNormalizedPropTypes(benefitPropTypesIntl)
};

export const headerPropTypes = {
  id: propTypes.id,
  slug: propTypes.slug,
  title: propTypes.title,
  company: PropTypes.shape(companyPropTypes)
};

export const fulltextPropTypes = {
  ...propTypes,
  monthlyRate: PropTypes.number,
  monthlyRateUpTo: PropTypes.number,
  locations: PropTypes.arrayOf(PropTypes.shape({
    city: PropTypes.string,
    citySlug: PropTypes.string,
    country: PropTypes.string,
    countrySlug: PropTypes.string
  }))
};

export const propTypesWithStats = {
  ...propTypes,
  stats: PropTypes.shape({
    applicants: PropTypes.shape({
      total: PropTypes.number,
      unread: PropTypes.number,
      last: PropTypes.string
    }),
    messages: PropTypes.shape({
      total: PropTypes.number,
      unread: PropTypes.number,
      last: PropTypes.string
    })
  })
};

export default { defaultProps, propTypes };
